import { practiceDataService } from "./practice-data.service";
import { uuid } from "@/lib/id";

/**
 * Karteikarten eines Themas (liegen in `practice_data.flashcards`, siehe
 * SCHEMA.md).
 *
 * Alle Karten liegen in EINEM Array, egal ob von Hand getippt, aus einem
 * `::: karteikarte`-Block der Notizen übernommen oder von der KI
 * vorgeschlagen. Herkunft merkt nur `origin`. Grund: verwaltet wird an genau
 * einer Stelle – wie bei den Hausaufgaben aus Notizblöcken. Zwei getrennte
 * Bestände hätten sofort die Frage aufgeworfen, welcher gilt.
 */

export type CardOrigin = "manuell" | "notiz" | "ki";

export interface Flashcard {
  id: string;
  topic: string;
  front: string;
  back: string;
  hint: string;
  position: number;
  box: Box;
  /** YYYY-MM-DD, ab wann die Karte wieder drankommt. */
  due: string;
  seen: number;
  lapses: number;
  origin: CardOrigin;
}

export type Box = 1 | 2 | 3 | 4 | 5;

export const BOX_DAYS: Record<Box, number> = { 1: 0, 2: 2, 3: 7, 4: 14, 5: 30 };
export const BOX_WHEN: Record<Box, string> = {
  1: "täglich",
  2: "alle 2–3 Tage",
  3: "wöchentlich",
  4: "alle 2 Wochen",
  5: "monatlich",
};

const todayIso = () => new Date().toISOString().slice(0, 10);

function newId(): string {
  return uuid();
}

function clampBox(v: unknown): Box {
  const n = Math.round(Number(v));
  return (n >= 1 && n <= 5 ? n : 1) as Box;
}

function toCard(r: Record<string, unknown>, topicId: string): Flashcard {
  return {
    id: String(r.id ?? ""),
    topic: topicId,
    front: String(r.front ?? ""),
    back: String(r.back ?? ""),
    hint: String(r.hint ?? ""),
    position: Number(r.position ?? 0),
    box: clampBox(r.box),
    due: String(r.due ?? ""),
    seen: Number(r.seen ?? 0),
    lapses: Number(r.lapses ?? 0),
    origin: (["manuell", "notiz", "ki"].includes(String(r.origin)) ? String(r.origin) : "manuell") as CardOrigin,
  };
}

function fromCard(c: Flashcard): Record<string, unknown> {
  const { topic: _topic, ...rest } = c;
  return rest;
}

/** Eine Zeile aus der Verwaltung – ohne Lernstand. */
export interface CardDraft {
  id?: string;
  front: string;
  back: string;
  hint?: string;
  origin?: CardOrigin;
}

export const flashcardsService = {
  async listByTopic(topicId: string): Promise<Flashcard[]> {
    const { flashcards } = await practiceDataService.read(topicId);
    return flashcards.map((r) => toCard(r, topicId)).sort((a, b) => a.position - b.position);
  },

  /**
   * Bestand auf die übergebene Liste bringen: neue anlegen, geänderte
   * aktualisieren, fehlende löschen. Die Reihenfolge der Liste ist die neue
   * `position`.
   *
   * Ein einzelner Speicher-Aufruf für den ganzen Stapel statt Einzelaktionen
   * beim Tippen – sonst schreibt jede Taste in die Datenbank.
   */
  async replaceAll(topicId: string, drafts: CardDraft[]): Promise<Flashcard[]> {
    const existing = await this.listByTopic(topicId);
    const byId = new Map(existing.map((c) => [c.id, c]));
    const out: Flashcard[] = [];

    for (const [i, d] of drafts.entries()) {
      const front = d.front.trim();
      const back = d.back.trim();
      if (!front) continue;

      const old = d.id ? byId.get(d.id) : undefined;
      if (old) {
        const unchanged =
          old.front === front && old.back === back && (old.hint ?? "") === (d.hint ?? "") && old.position === i;
        if (unchanged) {
          out.push(old);
          continue;
        }
        // Wird die Rückseite geändert, ist der alte Lernstand nicht mehr
        // gültig – die Karte fragt ja etwas anderes ab.
        const reset = old.back !== back;
        out.push({
          ...old,
          front,
          back,
          hint: d.hint ?? "",
          position: i,
          ...(reset ? { box: 1, due: todayIso() } : {}),
        });
      } else {
        out.push({
          id: newId(),
          topic: topicId,
          front,
          back,
          hint: d.hint ?? "",
          position: i,
          box: 1,
          due: todayIso(),
          seen: 0,
          lapses: 0,
          origin: d.origin ?? "manuell",
        });
      }
    }

    await practiceDataService.write(topicId, { flashcards: out.map(fromCard) });
    return out;
  },

  async sort(card: Flashcard, box: Box): Promise<Flashcard> {
    const days = BOX_DAYS[box];
    const due = new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
    const updated: Flashcard = {
      ...card,
      box,
      due,
      seen: card.seen + 1,
      lapses: box === 1 && card.box > 1 ? card.lapses + 1 : card.lapses,
    };
    const cards = await this.listByTopic(card.topic);
    const next = cards.map((c) => (c.id === card.id ? updated : c));
    await practiceDataService.write(card.topic, { flashcards: next.map(fromCard) });
    return updated;
  },

  grade(card: Flashcard, ok: boolean): Promise<Flashcard> {
    return this.sort(card, ok ? (Math.min(5, card.box + 1) as Box) : 1);
  },

  due(cards: Flashcard[]): Flashcard[] {
    const today = todayIso();
    return cards.filter((c) => !c.due || c.due <= today);
  },
};
